import { BillingModule } from './billing/billing.module';
import { PortfoliosModule } from './portfolios/portfolios.module';
import { SubscribersModule } from './subscribers/subscribers.module';
import { ProductPurchasesModule } from './product-purchases/product-purchases.module';
import { HrMissingTranslationHandler } from './missing-translation-handler';
import { JobseekersModule } from './jobseekers/jobseekers.module';
import { CustomersModule } from './customers/customers.module';
import { BrowserModule } from '@angular/platform-browser';
import { inject, NgModule, PLATFORM_ID, provideAppInitializer, TransferState } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MenubarModule } from 'primeng/menubar';

import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_BASE_HREF, isPlatformBrowser } from '@angular/common';
import { CoreModule } from './core/core.module';
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SharedModule } from './shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';

import { AppComponent } from './app.component';
import { AppMenuComponent } from './app-menu/app-menu.component';
import { AnnouncementsModule } from './announcements/announcements.module';
import { StatsAndReportsModule } from './stats-and-reports/stats-and-reports.module';

import { DashboardModule } from './dashboard/dashboard.module';
import { SettingsModule } from './settings/settings.module';
import { SalesModule } from './sales/sales.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { AnalyticsService, CookieModule, EnvService, LogginService, LoggingModule, StaticFeatureFlagService, URL_CONFIG_TOKEN, UrlConfigService } from '@hrra/core';
import { AngularSvgIconModule, SvgLoader } from 'angular-svg-icon';
import { SvgBrowserLoader } from './shared/services/svg-browser-loader';
import { translateBrowserLoaderFactory } from './shared/translate-browser.loader';
import { providePrimeNG } from 'primeng/config';
import { provideNgxWebstorage, withLocalStorage } from 'ngx-webstorage';
import { admin } from './primeNg-theme/admin';
import { catchError, tap, throwError } from 'rxjs';

import { AppConfig } from './core/app-config';
import { adminUrlConfig, UrlConfigContainer } from './core/url-config';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [AppComponent, AppMenuComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    CoreModule,
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateBrowserLoaderFactory,
        deps: [HttpClient, TransferState, EnvService],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: HrMissingTranslationHandler,
      },
    }),
    DashboardModule,
    //feature module imports
    JobseekersModule,
    CustomersModule,
    SubscribersModule,
    AnnouncementsModule,
    ProductPurchasesModule,
    StatsAndReportsModule,
    PortfoliosModule,
    BillingModule,
    SettingsModule,
    SalesModule,
    //end feature module imports
    AppRoutingModule,
    //BrowserAnimationsModule,
    CheckboxModule,
    NgbModule,
    ToastModule,
    MenubarModule,
    CookieModule.withOptions(),
    LoggingModule.forBrowser(),
    AngularSvgIconModule.forRoot({
      loader: {
        provide: SvgLoader,
        useFactory: svgLoaderFactory,
        deps: [HttpClient, TransferState],
      },
    }),
  ],
  providers: [
    provideAppInitializer(appInitializer),
    { provide: URL_CONFIG_TOKEN, useValue: adminUrlConfig },
    {
      provide: APP_BASE_HREF,
      useFactory: (env: EnvService) => {
        let result = env.appBaseHref;
        result = window.location.origin + env.appBaseHref;
        return result;
      },
      deps: [EnvService],
    },
    MessageService,
    provideHttpClient(withInterceptorsFromDi()),
    provideNgxWebstorage(withLocalStorage()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: admin,
        options: {
          darkModeSelector: false || 'none',
        },
      },
    }),
  ],
})
export class AppModule {}

export function svgLoaderFactory(
  http: HttpClient,
  transferState: TransferState
) {
  return new SvgBrowserLoader(transferState, http);
}

export function appInitializer() {
  const httpClient = inject(HttpClient);
  const platformId = inject(PLATFORM_ID);
  const envService = inject(EnvService);
  const urlConfigService = inject(UrlConfigService<UrlConfigContainer>);
  const logService = inject(LogginService);
  const staticFeatureFlagService = inject(StaticFeatureFlagService);
  const analyticsService = inject(AnalyticsService);

  if (!isPlatformBrowser(platformId)) {
    logService.logger.warn('Admin app should only run in browser');
    return;
  }

  const host = window.location.host;
  const protocol = window.location.protocol;
  const baseUrl = `${protocol}//${host}`;

  return httpClient.get<AppConfig>(`${baseUrl}/assets/conf/api.json`)
    .pipe(
      tap(({ logging, mainConfig, cacheConfig, staticFeatureFlags, analyticsConfig }) => {
        envService.apiConfig = mainConfig.apiConfig;
        envService.cacheConfig = cacheConfig;
        envService.appBaseHref = mainConfig.appBaseHref;
        envService.appVersion = mainConfig.appVersion;
        envService.logLevel = mainConfig.logLevel || logging.level;
        envService.analyticsConfig = analyticsConfig;

        staticFeatureFlagService.init(staticFeatureFlags);
        urlConfigService.init();
        analyticsService.init();

        logService.logger.info('Admin app config loaded');
      }),
      catchError(err => {
        logService.logger.error('Error loading admin config', err);
        return throwError(() => err);
      })
    );
}
